import { Avatar } from "@dropbox/dig-components/avatar";
import { Box } from "@dropbox/dig-foundations";
import { UserProfileHoverCard } from "@mirage/mosaics/BaseProfilePage/UserProfileHoverCard";
import { usePersonFromOrgChartByEmail } from "@mirage/service-people/hooks";
import classnames from "classnames";
import styles from "./AvatarCluster.module.css";

import type { AvatarSizes } from "@dropbox/dig-components/avatar";

type AvatarClusterSize = "small" | "medium";

type AvatarProps = {
  src?: string;
  email?: string;
  initials?: string;
  isGuest?: boolean;
  hasNoOutline?: boolean;
  className?: string;
};

type AvatarClusterProps = {
  // an list of up to 3 image sources to use in the avatar
  avatars: AvatarProps[];
  size?: AvatarClusterSize;
};

const AvatarWithHoverCard = ({
  props,
  className,
  size,
}: {
  props: AvatarProps;
  className?: string;
  size: AvatarSizes;
}) => {
  const defaultAvatar = (
    <Avatar
      size={size}
      className={className} // apply to avatar
      src={props.src}
      hasNoOutline={!!props.hasNoOutline}
    >
      {props.initials}
    </Avatar>
  );

  // If avatar has hover card, we need to apply classes to the hover card
  // container to properly stack the avatar cluster
  const hasHoverCard = usePersonFromOrgChartByEmail(props.email);
  if (hasHoverCard) {
    return (
      <UserProfileHoverCard
        personEmail={props.email}
        className={className} // apply to hover card container
        fallbackChildren={defaultAvatar} // show default avatar while hover cards are loading
      >
        <Avatar
          size={size}
          src={props.src}
          hasNoOutline={!!props.hasNoOutline}
          style={{ margin: 0 }}
        >
          {props.initials}
        </Avatar>
      </UserProfileHoverCard>
    );
  }
  return defaultAvatar;
};

export const AvatarCluster = ({
  avatars,
  size = "medium",
}: AvatarClusterProps) => {
  const sizeMap = sizesMap[size] || sizesMap.medium;
  const sizes = sizeMap[avatars.length];
  const countClassName = { 1: styles.one, 2: styles.two, 3: styles.three }[
    avatars.length
  ];

  const avatarPile = avatars.map((props, i) => {
    const size = sizes[i];
    const className = classnames(
      styles.avatar,
      countClassName,
      props.className,
    );
    const isGuest = props.isGuest || false;
    if (isGuest) {
      return (
        <Avatar
          key={i}
          size={size}
          className={className}
          hasNoOutline={!!props.hasNoOutline}
          isGuest
        ></Avatar>
      );
    }
    return (
      <AvatarWithHoverCard
        key={i}
        props={props}
        className={className}
        size={size}
      />
    );
  });

  return (
    <Box className={classnames(styles.container, styles[`size-${size}`])}>
      {avatarPile}
    </Box>
  );
};

const sizesMap: Record<AvatarClusterSize, Record<number, AvatarSizes[]>> = {
  small: {
    1: ["medium"],
    2: ["small", "small"],
    3: ["xsmall", "xsmall", "xsmall"],
  },
  medium: {
    1: ["large"],
    2: ["medium", "medium"],
    3: ["small", "xsmall", "xsmall"],
  },
};
